import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { FeatureFlagService } from '../../services/feature-flag';

/** Shape returned by GET /api/v1/urls/dashboard-summary */
interface DashboardSummary {
  hasData: boolean;
  hasComparisons: boolean;
  hasDomainData: boolean;
  totalAnalyses: number;
  recentComparisons: RecentComparison[];
  domains: DomainOverview[];
}

interface RecentComparison {
  id: string;
  yourPageUrl: string;
  competitorPageUrl: string;
  startedAt: string;
  completedAt: string | null;
  totalCost: number;
  status: 'completed' | 'in-progress' | 'error';
}

interface DomainOverview {
  domain: string;
  topicCount: number;
  ownedTopics: number;
  maxAuthority: number;
  totalPages: number;
}

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './dashboard.html',
  styleUrl: './dashboard.css'
})
export class DashboardComponent implements OnInit {
  private readonly API_URL = environment.apiUrl + '/v1';

  loading = true;
  error: string | null = null;
  summary: DashboardSummary | null = null;

  constructor(
    private http: HttpClient,
    public featureFlags: FeatureFlagService
  ) {}

  ngOnInit() {
    this.loadDashboard();
  }

  loadDashboard() {
    this.loading = true;
    this.error = null;

    this.http.get<{ success: boolean; data: DashboardSummary }>(`${this.API_URL}/urls/dashboard-summary`)
      .subscribe({
        next: (res) => {
          this.summary = res.data;
          this.loading = false;
        },
        error: (err) => {
          console.error('Failed to load dashboard:', err);
          this.error = 'Could not load dashboard data. Is the backend running?';
          this.loading = false;
        }
      });
  }

  /** Whether the dashboard should show the "empty / new user" state */
  get isEmpty(): boolean {
    return !this.summary?.hasData;
  }

  /** Format a URL for display (truncate long URLs) */
  formatUrl(url: string): string {
    try {
      const u = new URL(url);
      const path = u.pathname + u.search;
      const display = u.hostname + (path.length > 1 ? path : '');
      return display.length > 60 ? display.slice(0, 57) + '…' : display;
    } catch {
      return url.length > 60 ? url.slice(0, 57) + '…' : url;
    }
  }

  /** Relative time label (e.g. "2 hours ago") */
  timeAgo(dateStr: string): string {
    const now = Date.now();
    const then = new Date(dateStr).getTime();
    const diffMs = now - then;
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    const diffDay = Math.floor(diffHr / 24);
    if (diffDay < 30) return `${diffDay}d ago`;
    return new Date(dateStr).toLocaleDateString();
  }

  /** Status badge class */
  statusClass(status: string): string {
    switch (status) {
      case 'completed': return 'badge-success';
      case 'error': return 'badge-error';
      default: return 'badge-pending';
    }
  }

  /** Authority score color class */
  authorityClass(score: number): string {
    if (score >= 70) return 'authority-high';
    if (score >= 40) return 'authority-mid';
    return 'authority-low';
  }
}
